import { useT } from '@/core/I18nProvider';

import type { ReactElement } from 'react';

/**
 * B29.1 slice (b) — the customer-facing data-flow diagram on `/trust`.
 *
 * One conversation turn, end to end, as an ORDERED list: procurement readers
 * (and assistive tech) get the sequence from `<ol>` semantics rather than
 * decorative arrows, matching the TrustHub doctrine of copy-pasteable text
 * over graphics. Each node carries two chips:
 *
 *   - **storage** — `Stored` (persisted at rest) vs `Transient` (processed,
 *     not retained by that node);
 *   - **region** — `EU (Frankfurt)`. Every node is now in-region, including
 *     inference: post-ADR-0111 the default chat-completion provider is Claude
 *     on Amazon Bedrock in the EU (`data-residency.ts` → `aiInferenceEuByDefault`).
 *     The inference node's body notes the optional non-EU failover in prose.
 *
 * Chip colour is decorative only — the label text carries the information
 * (WCAG 1.4.1).
 */

type StorageChip = 'stored' | 'transient';
type RegionChip = 'eu';

interface FlowNode {
  /** Key part under `trusthub.dataflow.node.<id>`. */
  readonly id: string;
  readonly storage: StorageChip;
  readonly region: RegionChip;
}

const NODES: readonly FlowNode[] = [
  { id: 'message', storage: 'transient', region: 'eu' },
  { id: 'storage', storage: 'stored', region: 'eu' },
  { id: 'retrieval', storage: 'transient', region: 'eu' },
  { id: 'inference', storage: 'transient', region: 'eu' },
  { id: 'reply', storage: 'transient', region: 'eu' },
];

// Semantic tokens only (AF-495 colour purity) — the ComplianceCard badge
// taxonomy: positive → success, attention → warning, neutral-info → info.
const CHIP_BASE = 'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold';

const STORAGE_CHIP_CLASS: Record<StorageChip, string> = {
  stored: `${CHIP_BASE} bg-success/15 text-success-foreground`,
  transient: `${CHIP_BASE} bg-[color:var(--bd-sunk)] text-[color:var(--bd-muted)]`,
};

const REGION_CHIP_CLASS: Record<RegionChip, string> = {
  eu: `${CHIP_BASE} bg-info/15 text-info-foreground`,
};

export function DataFlowDiagram(): ReactElement {
  const { t } = useT();
  return (
    <div>
      <p className="mb-4 text-[color:var(--bd-muted)]">{t('trusthub.dataflow.intro')}</p>
      <ol className="space-y-3">
        {NODES.map((node, index) => (
          <li key={node.id} className="rounded-lg border border-[color:var(--bd-line)] p-4">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background"
              >
                {index + 1}
              </span>
              <h3 className="text-base font-semibold">
                {t(`trusthub.dataflow.node.${node.id}.title`)}
              </h3>
              <span className={STORAGE_CHIP_CLASS[node.storage]}>
                {t(`trusthub.dataflow.chip.${node.storage}`)}
              </span>
              <span className={REGION_CHIP_CLASS[node.region]}>
                {t(`trusthub.dataflow.chip.${node.region}`)}
              </span>
            </div>
            <p className="ps-8 text-sm text-[color:var(--bd-muted)]">
              {t(`trusthub.dataflow.node.${node.id}.body`)}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
